import type { CSSProperties } from 'react';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';
import { fmtDate, fmtDateTime } from '../../lib/format.js';
import { requestNotificationPermission } from '../pwa/notification-utils.js';
import type { ToastType } from '../../types';
import type { TFunction } from '../../i18n/index';

/**
 * Benachrichtigungs-Eintrag (flexibles Shape: teils aus DB, teils Frontend).
 */
interface NotifItem {
  id?: string;
  title?: string;
  sub?: string;
  read?: boolean;
  isEmail?: boolean;
  created_at?: string;
  datum?: string;
}

/** Admin-Nachrichten (Plattform-Broadcasts). */
interface AdminMsg {
  id?: string;
  gelesen?: boolean;
  von?: string;
  text?: string;
  datum?: string;
}

interface RenderNotifDropdownProps {
  t: TFunction;
  unreadCount: number;
  markAllRead: () => void;
  meineNotifs: NotifItem[];
  adminMsgs: AdminMsg[];
  markAdminMsgRead: (msgId: string) => void;
  deleteAdminMsg: (msgId: string) => void;
  clickNotif: (id: string) => void;
  openToast: (msg: string, type?: ToastType) => void;
}

/**
 * renderNotifDropdown — Benachrichtigungs-Dropdown (Topbar) mit Admin-Nachrichten
 * und User-Notifications.
 *
 * Zeigt maximal 5 Admin-Nachrichten und 12 persönliche Notifications. Bietet
 * "Alle gelesen markieren" und (falls Browser-Permission noch nicht erteilt)
 * einen Button zum Aktivieren von Push-Notifications.
 */
export function renderNotifDropdown({
  t,
  unreadCount,
  markAllRead,
  meineNotifs,
  adminMsgs,
  markAdminMsgRead,
  deleteAdminMsg,
  clickNotif,
  openToast,
}: RenderNotifDropdownProps) {
  return (
    <div
      className="notif-dropdown"
      style={{
        position: 'absolute' as const,
        top: 'calc(100% + 8px)',
        right: 0,
        width: 340,
        maxHeight: 420,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,.20)',
        zIndex: 500,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        animation: 'dropdownSlide .25s cubic-bezier(.2,0,.1,1)',
      }}>
      {/* Header */}
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
        <span
          style={{
            fontFamily: "'DM Sans',sans-serif",
            fontWeight: 700,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
          <Icon name="bell" size={16} />
          {unreadCount > 0 &&
            <span
              style={{
                background: '#ef4444',
                color: '#fff',
                borderRadius: 20,
                padding: '1px 7px',
                fontSize: 10,
                marginLeft: 6,
              }}>
              {unreadCount}
            </span>}
        </span>
        <button
          onClick={markAllRead}
          style={{
            fontSize: 11,
            color: 'var(--accent)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
          }}>
          <Icon name="check" size={14} />
        </button>
        {'Notification' in window &&
          Notification.permission !== 'granted' &&
          <button
            onClick={function () {
              requestNotificationPermission(t, openToast);
            }}
            style={{
              fontSize: 10,
              color: 'var(--amber)',
              background: 'rgba(245,158,11,.1)',
              border: '1px solid rgba(245,158,11,.2)',
              borderRadius: 8,
              padding: '2px 8px',
              cursor: 'pointer',
              fontWeight: 700,
              fontFamily: "'DM Sans',sans-serif",
              marginLeft: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}>
            <Icon name="bell" size={10} color="var(--amber)" />
            {' '}
            {t('notifPermission')}
          </button>}
      </div>
      {/* Content */}
      <div style={{ overflowY: 'auto' as const, flex: 1, maxHeight: 340 }}>
        {/* Admin Messages */}
        {adminMsgs.length > 0 &&
          <div>
            <div
              style={{
                padding: '8px 18px',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--amber)',
                textTransform: 'uppercase' as const,
                letterSpacing: 0.8,
                background: 'rgba(245,158,11,.06)',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}>
              <Icon name="info" size={10} color="var(--amber)" />
              {' '}
              {t('adminMsgSektion')}
            </div>
            {adminMsgs.slice(0, 5).map(function (m) {
              return (
                <div
                  key={m.id}
                  onClick={function () {
                    if (!m.gelesen) markAdminMsgRead(String(m.id));
                  }}
                  style={{
                    padding: '12px 18px',
                    borderBottom: '1px solid var(--border)',
                    borderLeft: m.gelesen ? '3px solid rgba(245,158,11,.3)' : '3px solid var(--amber)',
                    background: m.gelesen ? 'transparent' : 'rgba(245,158,11,.06)',
                    cursor: m.gelesen ? 'default' : 'pointer',
                  }}>
                  <div
                    style={{ ...(S.row as CSSProperties), gap: 6, fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                    <span
                      style={{
                        fontSize: 9,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: 'rgba(245,158,11,.15)',
                        color: 'var(--amber)',
                        fontWeight: 700,
                      }}>
                      {t('adminMsgBadge')}
                    </span>
                    {m.von || 'Admin'}
                    <button
                      onClick={function (e) {
                        e.stopPropagation();
                        deleteAdminMsg(String(m.id));
                      }}
                      title={t('adminMsgLoeschen')}
                      aria-label={t('adminMsgLoeschen')}
                      style={{
                        marginLeft: 'auto',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 2,
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--muted2)',
                      }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>
                    {m.text}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted2)', marginTop: 4 }}>
                    {fmtDate(m.datum)}
                  </div>
                </div>
              );
            })}
          </div>}
        {/* Empty state */}
        {meineNotifs.length === 0 && adminMsgs.length === 0
          ? <div
              style={{
                padding: 28,
                textAlign: 'center' as const,
                color: 'var(--muted)',
                fontSize: 13,
                display: 'flex',
                flexDirection: 'column' as const,
                alignItems: 'center',
                gap: 6,
              }}>
              <Icon name="bell" size={28} />
            </div>
          : null}
        {/* Notification Items */}
        {meineNotifs.slice(0, 12).map(function (n) {
          return (
            <div
              key={n.id}
              onClick={function () {
                clickNotif(n.id || '');
              }}
              style={{
                padding: '12px 18px',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                borderLeft: n.read ? 'none' : '3px solid var(--accent)',
                background: n.read ? 'transparent' : 'rgba(22,163,74,.03)',
              }}>
              <div
                style={{ ...(S.row as CSSProperties), gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                {n.isEmail &&
                  <span
                    style={{
                      fontSize: 9,
                      padding: '1px 6px',
                      borderRadius: 8,
                      background: 'rgba(22,163,74,.1)',
                      color: 'var(--accent)',
                      fontWeight: 700,
                    }}>
                    EMAIL
                  </span>}
                {n.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'pre-line' as const }}>
                {n.sub}
              </div>
              <div style={{ fontSize: 10, color: 'var(--muted2)', marginTop: 4 }}>
                {n.created_at
                  ? fmtDateTime(n.created_at, t.locale, {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : n.datum || ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
